import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { UnstyledTitle } from '~/elements/Title/Title';
import { DEV } from '~/utils/constants';
import * as styles from './Payment.css';

declare global {
  interface Window {
    Stripe?: (key: string) => {
      redirectToCheckout: (options: {
        lineItems: { price: string; quantity: number }[];
        mode: 'subscription';
        successUrl: string;
        cancelUrl: string;
      }) => Promise<{ error?: { message: string } }>;
    };
  }
}

type PlanTier = '1' | '2' | '3' | '4';

type PlanConfig = {
  monthly: Record<PlanTier, string>;
  yearly: Record<PlanTier, string>;
  default: string;
};

const devPlans: PlanConfig = {
  monthly: {
    '1': 'price_1N5bXNFlbw6IQ18QvOE8agqV',
    '2': 'plan_Fakd3dm3G4033f',
    '3': 'price_1N4SoHFlbw6IQ18QTratiOgZ',
    '4': 'price_1N5boAFlbw6IQ18QxmxagjZI',
  },
  yearly: {
    '1': 'price_1N4T0hFlbw6IQ18QigKBxcyG',
    '2': 'price_1N4T19Flbw6IQ18QwYEO4K1z',
    '3': 'price_1N4T2UFlbw6IQ18Qdr4OWKx6',
    '4': 'price_1N4T0hFlbw6IQ18QigKBxcyG',
  },
  default: 'price_1N5bXNFlbw6IQ18QvOE8agqV',
};

const prodPlans: PlanConfig = {
  monthly: {
    '1': 'price_1N5bOyFlbw6IQ18Q1XoYWj9I',
    '2': 'plan_Fakcmz21Df9XWm',
    '3': 'plan_FfjYaTicedBxTu',
    '4': 'price_1JzBhiFlbw6IQ18QINDrET6A',
  },
  yearly: {
    '1': 'plan_FtY1qItMclGx5h',
    '2': 'plan_FnIcecWlp2CCvC',
    '3': 'plan_FmxZ3VxbC0yhcl',
    '4': 'price_1N4TExFlbw6IQ18QnjckEq80',
  },
  default: 'price_1N5bOyFlbw6IQ18Q1XoYWj9I',
};

const stripeKey = DEV ? 'pk_test_gff3sTvGKup4BBAVZ15r8qo200a7gYh4R5' : 'pk_live_9cQPiGNgpFTYC0uTFeQsWXzr008ts8dQ5Z';

const baseUrl = DEV ? 'http://localhost:8000' : 'https://youneedawiki.com';

const userQuantities: Record<PlanTier, number> = {
  '1': 10,
  '2': 25,
  '3': 75,
  '4': 100,
};

const monthlyPrices: Record<PlanTier, number> = {
  '1': 19,
  '2': 49,
  '3': 99,
  '4': 129,
};

const yearlyPrices: Record<PlanTier, { original: number; discounted: number }> = {
  '1': { original: 228, discounted: 108 },
  '2': { original: 588, discounted: 528 },
  '3': { original: 1188, discounted: 1068 },
  '4': { original: 1548, discounted: 1390 },
};

export const Payment = () => {
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>('1');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [error, setError] = useState('');
  const [stripeLoaded, setStripeLoaded] = useState(!!window.Stripe);

  useEffect(() => {
    if (window.Stripe) return; // already loaded
    if (DEV) {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/clover/stripe.js';
      script.async = true;
      script.onload = () => setStripeLoaded(true);
      document.head.appendChild(script);
      return () => {
        script.remove();
      };
    }
    const script = document.querySelector<HTMLScriptElement>('script[src="https://js.stripe.com/clover/stripe.js"]');
    script?.addEventListener('load', () => setStripeLoaded(true), { once: true });
    return;
  }, []);

  const plans = DEV ? devPlans : prodPlans;

  const handleCheckout = async () => {
    setError('');

    if (!window.Stripe || !stripeLoaded) {
      setError('Payment system is loading. Please try again.');
      return;
    }

    const stripe = window.Stripe(stripeKey);
    const priceId = plans[billingCycle][selectedPlan] || plans.default;

    const result = await stripe.redirectToCheckout({
      lineItems: [{ price: priceId, quantity: 1 }],
      mode: 'subscription',
      successUrl: `${baseUrl}/app/success?session_id={CHECKOUT_SESSION_ID}`,
      cancelUrl: `${baseUrl}/app`,
    });

    if (result.error) {
      setError(result.error.message ?? 'An error occurred. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <Link to="/app" className={styles.backLink}>
          ← Back to wiki
        </Link>

        <div className={styles.header}>
          <img src="/img/icon/icon.svg" className={styles.logo} alt="You Need A Wiki logo" />
          <UnstyledTitle as="h2" className={styles.logoText}>
            YNAW
          </UnstyledTitle>
        </div>

        <UnstyledTitle as="h2" className={styles.title}>
          You Need A Wiki - Pro plan
        </UnstyledTitle>

        <button type="button" className={styles.purchaseButton} onClick={handleCheckout}>
          Purchase - Pro Plan
        </button>

        {error && <div className={styles.errorMessage}>{error}</div>}

        <div className={styles.pricingSection}>
          <div className={styles.pricingHeader}>
            <UnstyledTitle as="h3" className={styles.sectionTitle}>
              Pricing
            </UnstyledTitle>
            <select
              className={styles.select}
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value as PlanTier)}
            >
              <option value="1">10 users</option>
              <option value="2">25 users</option>
              <option value="3">75 users</option>
              <option value="4">100 users</option>
            </select>
          </div>

          {billingCycle === 'monthly' ? (
            <p className={styles.priceItem}>
              ${monthlyPrices[selectedPlan]} per month - {userQuantities[selectedPlan]} users.
            </p>
          ) : (
            <p className={styles.priceItem}>
              <span className={styles.strikethrough}>${yearlyPrices[selectedPlan].original}</span>$
              {yearlyPrices[selectedPlan].discounted} per year - {userQuantities[selectedPlan]} users
            </p>
          )}
        </div>

        <div className={styles.pricingSection}>
          <UnstyledTitle as="h3" className={styles.sectionTitle}>
            Features
          </UnstyledTitle>
          <ul className={styles.featuresList}>
            <li className={styles.featureItem}>Custom domain name alias</li>
            <li className={styles.featureItem}>Custom logo and sidebar color</li>
            <li className={styles.featureItem}>Search document content</li>
            <li className={styles.featureItem}>Unlimited wikis</li>
            <li className={styles.featureItem}>{userQuantities[selectedPlan]} editors, unlimited viewers</li>
          </ul>
        </div>

        <hr className={styles.separator} />

        <UnstyledTitle as="h3" className={styles.sectionTitle}>
          Refunds
        </UnstyledTitle>
        <p className={styles.refundSection}>
          If for any reason you are not happy we will happily refund the current months amount.
        </p>

        <hr className={styles.separator} />

        <button
          type="button"
          className={styles.switchLink}
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
        >
          Switch to {billingCycle === 'monthly' ? 'annual' : 'monthly'} pricing »
        </button>
      </div>
    </div>
  );
};
